import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import ReactResizeDetector from 'react-resize-detector';
import * as _ from 'lodash';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import FlexBox from 'components/FlexBox';
import injectReducer from 'utils/injectReducer';
import { makeSelectStepClaimSummary, makeSelectStepClaimSummaryDirty, makeSelectExchangeRates, makeSelectExchangeRatesLoading } from './selectors';
import reducer from './reducer';
import PageSectionHeader from '../components/PageSectionHeader';
import Textbox from '../components/Textbox';
import messages from './messages';
import {
  handleFormDataChange,
  resetFormGivePermissionState,
  fetchExchangeRates,
} from './actions';
import OptionsContainer from '../components/OptionsContainer';
import WizardNavigationButtons from '../components/WizardNavigationButtons';
import Select from '../components/Select';
import { saveFormData } from '../actions';
import { required, maxLength, validEmail } from '../../../utils/validation/validator';
import {
  ErrorMessage,
  Validate,
  ValidateGroup,
} from '../../../components/Validation/';
import H3 from '../../../components/H3';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import { makeSelectStepWhatHappened } from '../StepWhatHappened/selectors';
import { makeSelectStepCancellationAdditional } from '../StepCancellationAdditional/selectors';
import { makeSelectStepPersonalItems } from '../StepPersonalItems/selectors';
import { makeSelectStepOverseasMedical } from '../StepOverseasMedical/selectors';
import { makeSelectStepRentalVehicle } from '../StepRentalVehicle/selectors';
import { makeSelectStepOther } from '../StepOther/selectors';
import { makeSelectStepSupportingDocuments } from '../StepSupportingDocuments/selectors';
import { makeSelectStepTripCancellationAndTripInterruption } from '../StepTripCancellationAndTripInterruption/selectors';
import { makeSelectStepBaggageAndPersonalItems } from '../StepBaggageAndPersonalItems/selectors';
import { makeSelectStepMedicalExpenses } from '../StepMedicalExpenses/selectors';
import { makeSelectStepPaymentDetails } from '../StepPaymentDetails/selectors';
import { makeSelectStepBankDetails } from '../StepBankDetails/selectors';
import { makeSelectMedicalExpenseStep } from '../Trawick.NationWide.MedicalExpenseStep/selectors';
import { makeSelectTripCancellationStep } from '../Trawick.NationWide.TripCancellationStep/selectors';
import { makeSelectTripDelayStep } from '../Trawick.NationWide.TripDelayStep/selectors';
import { makeSelectTripInterruptionStep } from '../Trawick.NationWide.TripInterruptionStep/selectors';
import { makeSelectPetMedicalStep } from '../Trawick.NationWide.PetMedicalStep/selectors';
import { makeSelectPropertyDamageStep } from '../Trawick.NationWide.PropertyDamageStep/selectors';
import { makeSelectCollisionDamageStep } from '../Trawick.NationWide.CollisionDamageStep/selectors';
import { makeSelectMissedConnectionStep } from '../Trawick.NationWide.MissedConnectionStep/selectors';
import { makeSelectSportsEquipmentStep } from '../Trawick.NationWide.SportsEquipmentStep/selectors';
import { makeSelectPersonalItemStep } from '../Trawick.NationWide.PersonalItemStep/selectors';
import { makeSelectMedicalExpenseCFStep } from '../Trawick.CFTripCan.MedicalExpense/selectors';
import { makeSelectMedicalExpenseZurichStep } from '../Trawick.Zurich.MedicalExpense/selectors';
import { makeSelectItineraryChange } from '../Trawick.CFTripCan.ItineraryChange/selectors';
import { makeSelectBaggagePersonalEffects } from '../Trawick.CFTripCan.BaggagePersonalEffects/selectors';
import { makeSelectMedicalHardshipStep } from '../Trawick.DuesProtect.MedicalHardshipStep/selectors';
import { makeSelectJobLossHardshipStep } from '../Trawick.DuesProtect.JobLossHardshipStep/selectors';
import { makeSelectDisasterAssistanceHardshipStep } from '../Trawick.DuesProtect.DisasterAssistanceHardshipStep/selectors';
import { makeSelectStepTravelDelay } from '../StepTravelDelay/selectors';
import { makeSelectMedicalAndEvacuationSCTIStep } from '../Trawick.SCTI.MedicalAndEvacuation/selectors';
import { makeSelectChangesToYourJourneySCTIStep } from '../Trawick.SCTI.ChangesToYourJourney/selectors';
import { makeSelectBaggageAndPersonalItemsSCTIStep } from '../Trawick.SCTI.BaggageAndPersonalItems/selectors';
import { makeSelectCashBankCardsTravelDocumentsAndPassportsStep } from '../Trawick.SCTI.CashBankCardsTravelDocumentsAndPassports/selectors';
import { makeSelectPersonalAccidentSCTIStep } from '../Trawick.SCTI.PersonalAccident/selectors';
import { makeSelectRentalVehicleExcessSCTIStep } from '../Trawick.SCTI.RentalVehicleExcess/selectors';
import { makeSelectChangesToYourJourneyStep } from '../Trawick.ChangesToYourJourney/selectors';
import { makeSelectStepBaggageAndPersonalItemsStarr } from '../Trawick.Starr.StepBaggageAndPersonalItems/selectors';
import { makeSelectStepOtherStarr } from '../Trawick.Starr.StepOther/selectors';
import { makeSelectCollisionDamageStarrStep } from '../Trawick.Starr.StepCollisionDamage/selectors';
import { makeSelectMedicalExpensesTugoStep } from '../Tugo.MedicalExpenses/selectors';
import { makeSelectStepOtherTugo } from '../Tugo.StepOther/selectors';
import { makeSelectBaseVictorsMedicalTugo } from '../Tugo.BaseVictorsMedical/selectors';
import DocumentTable from '../StepDeclaration/components/DocumentTable';
import DocumentTableDuesProtect from './components/DocumentTableDuesProtect';
import DocumentTableMultiInsureds from './components/DocumentTableMultiInsureds';
import {
  listCodeForDuesProtect,
  stepOtherExpenseLookup,
  stepTripCancellationAndTripInterruptionExpenseLookup,
  stepMedicalAndEvacuationLookup,
  stepChangesToYourJourneyLookup,
  stepBaggageAndPersonalItemsSCTILookup,
  stepBaggageAndPersonalItemsExpenseLookup,
  stepMedicalExpensesTugoLookup,
  stepOtherTugoLookup,
  stepCollisionDamageExpenseLookup,
  stepCollisionDamageStarrLookup,
  stepBaggageAndPersonalItemsStarrLookup,
  stepOtherStarrLookup,
  stepCashBankCardsTravelDocumentsAndPassportsLookup,
} from '../dataMapper/claimCodeMapper';
import stepChangesToYourJourneyDataMapper from '../dataMapper/stepChangesToYourJourneyDataMapper';
import stepMedicalExpenseCFDataMapper from '../dataMapper/stepMedicalExpenseCFDataMapper';
import stepBaseVictorsMedicalTugoDataMapper from '../dataMapper/stepBaseVictorsMedicalTugoDataMapper';
import { makeSelectLoginInfo } from '../../Login/selectors';
import Section from '../components/Section';
import { makeSelectAppClaimType, makeSelectJSONVersion, makeSelectIsClaimGroupAllowed, makeSelectShowZurichUHC, makeSelectClaimantId, makeSelectInsuredsList, makeSelectInitClaimTypeList, makeSelectIsClaimForFieldEnabled, makeSelectPolicyTypeCode, makeSelectFormHasChanges } from '../../App/selectors';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import P from '../../../components/P';
import { appConfig } from '../../../configureApp';
import { setFormHasChanges } from '../../App/actions';

class StepClaimSummary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
      errors: [],
      listDocuments: [],
    };

    this.validatorGroup = new ValidateGroup();
    this.saveButtonId = _.uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;

    this.onResize = this.onResize.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;

    const listDocuments = this.mapDocumentItems();
    this.setState({ listDocuments });

    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.checkFormError();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.state.validationCounter += 1;
      this.validatorGroup.checkValidation();
    }

    if (!nextProps.loading) {
      this.clickedItemId = '';
    }

    // Trigger exchange rate fetch when this step becomes active
    if (currentStep !== nextProps.currentStep && nextProps.currentStep === step) {
      this.triggerExchangeRateFetch();
    }
  }
  onResize = () => {
    const { adjust } = this.props;
    if (adjust) {
      adjust();
    }
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.handleFormDataSave();
    this.nextButton.focus();
  };

  handleChange = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (target.type === 'radio') {
      this.props.adjust();
    }

    this.props.onFormDataChange(propertyName, value);
    this.props.setFormHasChanges(true);
    // this.props.makeDirty(1);
  };

  handleDayChange = (propertyName) => (day) => {
    const localDate = removeTimeZoneFromDate(day);
    this.props.onFormDataChange(propertyName, localDate);
    this.props.setFormHasChanges(true);
    // this.props.makeDirty(1);
  };
  
  handleContactNumberChange = (propertyName) => (event) => {
    const target = event.target;
    const value = target.value;
    // Regex to allow only numbers, +, and prevent multiple + signs
    const regex = /^[0-9+]*$/;
    const hasMultiplePlus = (value.match(/\+/g) || []).length > 1;
    
    if ((regex.test(value) && !hasMultiplePlus) || value === '') {
      this.props.onFormDataChange(propertyName, value);
      this.props.setFormHasChanges(true);
    }
  };

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }
    this.checkFormError();

    this.handleFormDataSave();
    this.props.next();
  };

  checkFormError() {
    const { warningMissingRequiredDocument } = this.props.stepSupportingDocumentsData;
    const valid = this.validatorGroup.checkValidation() && warningMissingRequiredDocument === '';
    const filedsValidation = this.validatorGroup.checkValidation();
    this.props.onFormDataChange('form.validated', filedsValidation);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  handleFormDataSave = () => {
    this.props.onFormDataSave();
    this.props.setFormHasChanges(false);
  };

  handleUncheckedFormState = (field, resetAction) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    if (value === 'false') {
      resetAction();
    }

    this.handleChange(field)(event);
  };

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') {
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
    }
  };

  handleChangePhoneNumber = (propertyName) => (event) => {
    const target = event.target;
    const regex = /^[0-9-]+$/;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (regex.test(value) || value === '') {
      this.props.onFormDataChange(propertyName, value);
      this.props.setFormHasChanges(true);
    }
  };

  isValidEmail(email) {
    if (email !== '') {
      return /\S+@\S+\.\S+/.test(email);
    }
    return true;
  }

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  triggerExchangeRateFetch = () => {
    try {
      // Collect all expenses from the apiContractMapper (same pattern as submission)
      // Use a simplified approach: extract expenses from the allBenefitsExpensesDataMapper
      const allBenefitsExpensesDataMapper = require('../dataMapper/allBenefitsExpensesDataMapper').default;
      const expenses = allBenefitsExpensesDataMapper.getAllExpensesFlat(this.props);
      if (expenses && expenses.length > 0) {
        this.props.onFetchExchangeRates(expenses);
      }
    } catch (e) {
      // Silently fail - exchange rates are optional enhancement
      console.warn('Failed to trigger exchange rate fetch:', e);
    }
  };

  summaryFormValue() {
    return _.mapValues(this.props.form, (prop) => prop || 'N/A');
  }

  mapDocumentItems() {
    const { claimTypesInit, policyTypeCode } = this.props;
    const { selectedBenefits } = this.props.stepWhatHappenedData;
    // Trawick Benefits
    const selectedBenefitsChangesToYourJourney = stepChangesToYourJourneyDataMapper.mapToClaimExpensesLevel(this.props.stepChangesToYourJourneyData, claimTypesInit);
    const selectedBenefitsMedicalExpense = stepMedicalExpenseCFDataMapper.mapToClaimExpensesLevel(this.props.stepMedicalExpenseCFData, claimTypesInit);
    const isInternationalStudent = policyTypeCode === 'INT-ST-NZ';
    const policyTypeVisitingNZ = ['VISIT-NZ', 'VISIT-NZ-PI'];
    const isVisitingNewZealand = policyTypeVisitingNZ.includes(policyTypeCode);
    const policyTypeWorkingOverseas = ['WORK-AU', 'WORK-NZ'];
    const isWorkingOverseas = policyTypeWorkingOverseas.includes(policyTypeCode);
    const isShowSubBenefits = isInternationalStudent || isVisitingNewZealand || isWorkingOverseas;
    const { travelInterruption, tripCancellation, tripCancellationForAnyReason } = this.props.stepTripCancellationAndTripInterruptionData;
    // const { tripCancellation: tripCancellationNewVersion, travelInterruption: travelInterruptionNewVersion, travelDelay: travelDelayNewVersion, missedConnection: missedConnectionNewVersion } = this.props.stepChangesToYourJourneyData;
    const { areYouClaimingForAccidentalDeathAndDismemberment, areYouClaimingForRepatriationExpenses, areYouClaimingForBurialExpenses, whatExpensesAreYouClaimingFor, whatAreYourExpensesRelatedTo } = this.props.stepOtherData;
    const { didAnAccompanyingPersonTravelFromYourHomeCountryWhilstYouWereHospitalised, wasTheDeathCausedByAnInjury, whatExpensesAreYouClaimingFor: whatExpensesAreYouClaimingForMedicalAndEvacuationSCTI, whatAreYourExpensesRelatedTo: MAEWhatAreYourExpensesRelatedTo } = this.props.stepMedicalAndEvacuationSCTIData;
    const { whatHappenedToYourBaggageAndPersonalItems, whatItemsAreYouClaimingFor } = this.props.stepBaggageAndPersonalItemsSCTIData;
    const { whatItemsAreYouClaimingFor: passportItemsClaiming } = this.props.stepCashBankCardsTravelDocumentsAndPassportsData;
    const { haveYouCanceledOrChangedYourJourneyBeforeLeaving, haveYouChangedYourJourneyAfterLeaving, wasTheUnexpectedEventRelatedToTheRelevantPersonsExistingHealthCondition,
      wereYouChargedACancellationFeeByTheTravelAgentPI, wereYouChargedACancellationFeeByTheTravelAgent, wereYouDelayedWhileTravelingToASpecialEvent, wereYouDelayedWhileTravelingToASpecialEventAL,
      didYouAlsoIncurTheseCostsAsAResultOfYourTravelDisruption, wasTheUnexpectedEventRelatedToTheRelevantPersonsExistingHealthConditionAL } = this.props.stepChangesToYourJourneySCTIData;
    // Trawick UI
    const { areYouClaimingForDamagedLostOrStolenLuggage, areYouClaimingForBaggageDelay } = this.props.stepBaggageAndPersonalItemsData;
    const { areYouClaimingForDamagedLostOrStolenLuggage: areYouClaimingForDamagedLostOrStolenLuggageStarr, areYouClaimingForBaggageDelay: areYouClaimingForBaggageDelayStarr } = this.props.stepBaggageAndPersonalItemsStarrData;
    const { areYouClaimingForAccidentalDeathAndDismemberment: areYouClaimingForAccidentalDeathAndDismembermentStarr, areYouClaimingForRepatriationExpenses: areYouClaimingForRepatriationExpensesStarr } = this.props.stepOtherStarrData;
    const { areYouClaimingForRentalCarDamage } = this.props.stepCollisionDamageData;
    const { areYouClaimingForRentalCarDamage: areYouClaimingForRentalCarDamageStarr } = this.props.stepCollisionDamageStarrData;

    const expensesPersonalLiabilitySelected = !!whatExpensesAreYouClaimingFor && typeof whatExpensesAreYouClaimingFor.toJS === 'function'
      ? whatExpensesAreYouClaimingFor.toJS()
      : whatExpensesAreYouClaimingFor || [];
    const whatAreYourExpensesRelatedToSelected = !!whatAreYourExpensesRelatedTo && typeof whatAreYourExpensesRelatedTo.toJS === 'function'
      ? whatAreYourExpensesRelatedTo.toJS()
      : whatAreYourExpensesRelatedTo || [];
    const isPersonalLiabilitySelected = expensesPersonalLiabilitySelected.includes('Personal Liability');
    const isPersonalAccidentSelected = expensesPersonalLiabilitySelected.includes('Personal Accident');
    const isLossOfIncomeCodeSelected = whatAreYourExpensesRelatedToSelected.includes('Loss of Income');
    const isTotalPermanentDisablementCodeSelected = whatAreYourExpensesRelatedToSelected.includes('Total permanent disablement');
    const isLossOfLifeCodeSelected = whatAreYourExpensesRelatedToSelected.includes('Loss of Life');
    const list = [...(!!selectedBenefits && typeof selectedBenefits.toJS === 'function') ? selectedBenefits.toJS() : (selectedBenefits || [])]
      .filter((x) => x.description !== 'Other'
        && x.description !== 'Trip Cancellation & Trip Interruption'
        && x.description !== 'Medical and evacuation'
        && x.description !== 'Baggage and personal items' // SCTI
        && x.description !== 'Baggage and Personal Items' // Trawick
        && x.description !== 'Changes to your journey'
        && x.description !== 'Medical Expenses'
        && x.description !== 'Cash, bank cards, travel documents and passports'
        && x.description !== 'Collision Damage'
        && x.description !== 'Medical Expenses'
        && x.description !== 'Medical Expense');

    // Collision Damage
    if (areYouClaimingForRentalCarDamage === 'true') {
      const code = stepCollisionDamageExpenseLookup.collisionDamage.claimCode;
      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === code && areYouClaimingForRentalCarDamage === 'true') {
            const benefit = this.mapQuestionItem(item, 'Collision Damage');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    if (areYouClaimingForRentalCarDamageStarr === 'true') {
      const code = stepCollisionDamageStarrLookup.collisionDamage.claimCode;
      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === code && areYouClaimingForRentalCarDamageStarr === 'true') {
            const benefit = this.mapQuestionItem(item, 'Collision Damage');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    // Baggage and personal items Trawick
    if (areYouClaimingForDamagedLostOrStolenLuggage === 'true' || areYouClaimingForBaggageDelay === 'true') {
      const code1 = stepBaggageAndPersonalItemsExpenseLookup.areYouClaimingForDamaged.claimCode;
      const code2 = stepBaggageAndPersonalItemsExpenseLookup.areYouClaimingForBaggageDelay.claimCode;
      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === code1 && areYouClaimingForDamagedLostOrStolenLuggage === 'true') {
            const benefit = this.mapQuestionItem(item, 'Baggage/Personal Effects');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === code2 && areYouClaimingForBaggageDelay === 'true') {
            const benefit = this.mapQuestionItem(item, 'Baggage delay');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    // Other Documents
    if ((areYouClaimingForRepatriationExpenses === 'true' || areYouClaimingForAccidentalDeathAndDismemberment === 'true' || areYouClaimingForBurialExpenses === 'true'
      || isPersonalLiabilitySelected || isPersonalAccidentSelected || isLossOfIncomeCodeSelected || isLossOfLifeCodeSelected || isTotalPermanentDisablementCodeSelected) && claimTypesInit !== undefined) {
      const code1 = stepOtherExpenseLookup.areYouClaimingForRepatriationExpenses.claimCode;
      const code2 = stepOtherExpenseLookup.areYouClaimingForAccidentalDeathAndDismemberment.claimCode;
      const code3 = stepOtherExpenseLookup.areYouClaimingForBurialExpenses.claimCode;
      const code4 = stepOtherExpenseLookup.personalLiability.claimCode;
      const code5 = stepOtherExpenseLookup.personalAccident.claimCode;
      const lossOfIncomeCode = stepOtherExpenseLookup.lossOfIncome.claimCode;
      const lossOfLifeCode = stepOtherExpenseLookup.lossOfLife.claimCode;
      const totalPermanentDisablementCode = stepOtherExpenseLookup.totalPermanentDisablement.claimCode;
      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === code1 && areYouClaimingForRepatriationExpenses === 'true') {
            const benefit = this.mapQuestionItem(item, 'Repatriation Expenses');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === code2 && areYouClaimingForAccidentalDeathAndDismemberment === 'true') {
            const benefit = this.mapQuestionItem(item, 'Accidental Death & Dismemberment');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === code3 && areYouClaimingForBurialExpenses === 'true') {
            const benefit = this.mapQuestionItem(item, 'Burial Expenses');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === code4 && isPersonalLiabilitySelected) {
            const benefit = this.mapQuestionItem(item, 'Personal Liability');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === code5 && isPersonalAccidentSelected) {
            const benefit = this.mapQuestionItem(item, 'Personal Accident');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === lossOfIncomeCode && isLossOfIncomeCodeSelected) {
            const benefit = this.mapQuestionItem(item, 'Loss of income');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === lossOfLifeCode && isLossOfLifeCodeSelected) {
            const benefit = this.mapQuestionItem(item, 'Loss of Life');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === totalPermanentDisablementCode && isTotalPermanentDisablementCodeSelected) {
            const benefit = this.mapQuestionItem(item, 'Total permanent disablement');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    // Trip Interruption
    if ((tripCancellation === 'true' || tripCancellationForAnyReason === 'true' || travelInterruption === 'true') && claimTypesInit !== undefined) {
      const code1 = stepTripCancellationAndTripInterruptionExpenseLookup.tripCancellation.claimCode;
      const code2 = stepTripCancellationAndTripInterruptionExpenseLookup.tripCancellationForAnyReason.claimCode;
      const code3 = stepTripCancellationAndTripInterruptionExpenseLookup.travelInterruption.claimCode;
      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === code1 && tripCancellation === 'true') {
            const benefit = this.mapQuestionItem(item, 'Trip Cancellation');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === code2 && tripCancellationForAnyReason === 'true') {
            const benefit = this.mapQuestionItem(item, 'Trip Cancellation For Any Reason');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === code3 && travelInterruption === 'true') {
            const benefit = this.mapQuestionItem(item, 'Trip Interruption');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    // Changes To Your Journey Trawick
    // if ((tripCancellationNewVersion === 'true' || travelInterruptionNewVersion === 'true' || travelDelayNewVersion === 'true' || missedConnectionNewVersion === 'true') && claimTypesInit !== undefined) {
    //   const code1 = stepChangesToYourJourneyTrawickLookup.tripCancellation.claimCode;
    //   const code2 = stepChangesToYourJourneyTrawickLookup.travelInterruption.claimCode;
    //   const code3 = stepChangesToYourJourneyTrawickLookup.travelDelay.claimCode;
    //   const code4 = stepChangesToYourJourneyTrawickLookup.missedConnection.claimCode;
    //   claimTypesInit.forEach(
    //     (item) => {
    //       if (item.claimCode === code1 && tripCancellationNewVersion === 'true') {
    //         const benefit = this.mapQuestionItem(item, 'Trip Cancellation');
    //         if (!!benefit) {
    //           list.push(benefit);
    //         }
    //       }
    //       if (item.claimCode === code2 && travelInterruptionNewVersion === 'true') {
    //         const benefit = this.mapQuestionItem(item, 'Trip Interruption');
    //         if (!!benefit) {
    //           list.push(benefit);
    //         }
    //       }
    //       if (item.claimCode === code3 && travelDelayNewVersion === 'true') {
    //         const benefit = this.mapQuestionItem(item, 'Travel Delay');
    //         if (!!benefit) {
    //           list.push(benefit);
    //         }
    //       }
    //       if (item.claimCode === code4 && missedConnectionNewVersion === 'true') {
    //         const benefit = this.mapQuestionItem(item, 'Missed Connections');
    //         if (!!benefit) {
    //           list.push(benefit);
    //         }
    //       }
    //     }
    //   );
    // }
    if (selectedBenefitsChangesToYourJourney.length > 0) {
      list.push(...selectedBenefitsChangesToYourJourney);
    }

    if (selectedBenefitsMedicalExpense.length > 0) {
      list.push(...selectedBenefitsMedicalExpense);
    }

    // Baggage and personal items Starr
    if ((areYouClaimingForDamagedLostOrStolenLuggageStarr === 'true' || areYouClaimingForBaggageDelayStarr === 'true') && claimTypesInit !== undefined) {
      const code1 = stepBaggageAndPersonalItemsStarrLookup.areYouClaimingForDamaged.claimCode;
      const code2 = stepBaggageAndPersonalItemsStarrLookup.areYouClaimingForBaggageDelay.claimCode;
      claimTypesInit.forEach((item) => {
        if (item.claimCode === code1 && areYouClaimingForDamagedLostOrStolenLuggageStarr === 'true') {
          const benefit = this.mapQuestionItem(item, 'Damaged/Lost/Stolen Luggage');
          if (!!benefit) {
            list.push(benefit);
          }
        }
        if (item.claimCode === code2 && areYouClaimingForBaggageDelayStarr === 'true') {
          const benefit = this.mapQuestionItem(item, 'Baggage Delay');
          if (!!benefit) {
            list.push(benefit);
          }
        }
      });
    }

    // Other Documents Starr
    if ((areYouClaimingForAccidentalDeathAndDismembermentStarr === 'true' || areYouClaimingForRepatriationExpensesStarr === 'true') && claimTypesInit !== undefined) {
      const code1 = stepOtherStarrLookup.areYouClaimingForAccidentalDeathAndDismemberment.claimCode;
      const code2 = stepOtherStarrLookup.areYouClaimingForRepatriationExpenses.claimCode;
      claimTypesInit.forEach((item) => {
        if (item.claimCode === code1 && areYouClaimingForAccidentalDeathAndDismembermentStarr === 'true') {
          const benefit = this.mapQuestionItem(item, 'Accidental Death & Dismemberment (Starr)');
          if (!!benefit) {
            list.push(benefit);
          }
        }
        if (item.claimCode === code2 && areYouClaimingForRepatriationExpensesStarr === 'true') {
          const benefit = this.mapQuestionItem(item, 'Repatriation Expenses (Starr)');
          if (!!benefit) {
            list.push(benefit);
          }
        }
      });
    }

    // Medical and Evacuation SCTI
    const medicalAndEvacuationSCTISelected = !!whatExpensesAreYouClaimingForMedicalAndEvacuationSCTI && typeof whatExpensesAreYouClaimingForMedicalAndEvacuationSCTI.toJS === 'function'
      ? whatExpensesAreYouClaimingForMedicalAndEvacuationSCTI.toJS()
      : whatExpensesAreYouClaimingForMedicalAndEvacuationSCTI || [];

    const MAEWhatAreYourExpensesRelatedToSelected = !!MAEWhatAreYourExpensesRelatedTo && typeof MAEWhatAreYourExpensesRelatedTo.toJS === 'function'
      ? MAEWhatAreYourExpensesRelatedTo.toJS()
      : MAEWhatAreYourExpensesRelatedTo || [];

    const isMedicalHospitalExpensesSelected = medicalAndEvacuationSCTISelected.includes('Medical & Hospital Expenses');
    const isDentalExpensesSelected = medicalAndEvacuationSCTISelected.includes('Dental Expenses');
    const isFuneralExpensesSelected = medicalAndEvacuationSCTISelected.includes('Funeral Expenses');
    const isExpensesarisingfromterrorismSelected = medicalAndEvacuationSCTISelected.includes('Expenses arising from terrorism');
    const isExtraTravelAndAccommodationSelected = MAEWhatAreYourExpensesRelatedToSelected.includes('Extra travel and accommodation');

    if ((didAnAccompanyingPersonTravelFromYourHomeCountryWhilstYouWereHospitalised === 'true' || wasTheDeathCausedByAnInjury === 'true' || isMedicalHospitalExpensesSelected || isDentalExpensesSelected
      || isFuneralExpensesSelected || isExpensesarisingfromterrorismSelected || isExtraTravelAndAccommodationSelected) && claimTypesInit !== undefined) {
      const medicalHospitalExpensesCode = stepMedicalAndEvacuationLookup.medicalHospitalExpenses.claimCode;
      const accompanyingPersonCode = stepMedicalAndEvacuationLookup.didAnAccompanyingPersonTravelFromYourHomeCountryWhilstYouWerehospitalised.claimCode;
      const funeralExpensesCode = stepMedicalAndEvacuationLookup.funeralExpenses.claimCode;
      const lossOfLifeCode = stepMedicalAndEvacuationLookup.wasTheDeathCausedByAnInjury.claimCode;
      const extraTravelAndAccommodationCode = stepMedicalAndEvacuationLookup.extraTravelAndAccommodation.claimCode;
      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === medicalHospitalExpensesCode && (isMedicalHospitalExpensesSelected || isDentalExpensesSelected
            || isExpensesarisingfromterrorismSelected || isFuneralExpensesSelected || isExtraTravelAndAccommodationSelected)) {
            const benefit = this.mapQuestionItem(item, 'Medical and evacuation');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === accompanyingPersonCode && didAnAccompanyingPersonTravelFromYourHomeCountryWhilstYouWereHospitalised === 'true') {
            const benefit = this.mapQuestionItem(item, 'Accompanying Person');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          // Funeral expenses
          if (item.claimCode === funeralExpensesCode && isFuneralExpensesSelected) {
            const benefit = this.mapQuestionItem(item, 'Funeral expenses or return of mortal remains');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          // Loss Of Life
          if (item.claimCode === lossOfLifeCode && wasTheDeathCausedByAnInjury === 'true') {
            const benefit = this.mapQuestionItem(item, 'Loss Of Life');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          // Extra travel and accommodation
          if (item.claimCode === extraTravelAndAccommodationCode && isExtraTravelAndAccommodationSelected && isShowSubBenefits) {
            const benefit = this.mapQuestionItem(item, 'Extra travel and accommodation');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    const baggageAndPersonalItemsSelected = !!whatHappenedToYourBaggageAndPersonalItems && typeof whatHappenedToYourBaggageAndPersonalItems.toJS === 'function'
      ? whatHappenedToYourBaggageAndPersonalItems.toJS()
      : whatHappenedToYourBaggageAndPersonalItems || [];

    const damagedItemsSelected = !!whatItemsAreYouClaimingFor && typeof whatItemsAreYouClaimingFor.toJS === 'function'
      ? whatItemsAreYouClaimingFor.toJS()
      : whatItemsAreYouClaimingFor || [];

    const isDaggageDelaySelected = baggageAndPersonalItemsSelected.includes('Baggage delay');
    const isEssentialMedicationSelected = damagedItemsSelected.includes('Essential medication');
    const isSpecifiedHighValueItemsSelected = damagedItemsSelected.includes('Specified high value items');
    const isBankCardsOrTravelDocumentsSelected = damagedItemsSelected.includes('Bank cards or travel documents');
    const isCashSelected = damagedItemsSelected.includes('Cash');
    const isJewelleryOrTraditionalWatchesSelected = damagedItemsSelected.includes('Jewellery or traditional watches');
    const isUnspecifiedLaptopSelected = damagedItemsSelected.includes('Laptops, tablets or cameras');
    const isOtherUnspecifiedItemsSelected = damagedItemsSelected.includes('Other unspecified items');
    const listPolicyHideParentDocuments = ['INT-COMP-AU', 'WORK-AU', 'INT-COMP-NZ', 'WORK-NZ', 'VISIT-NZ'];
    const isShowParentDocuments = !listPolicyHideParentDocuments.includes(policyTypeCode);

    if ((isDaggageDelaySelected || isEssentialMedicationSelected || isSpecifiedHighValueItemsSelected || isBankCardsOrTravelDocumentsSelected ||
      isCashSelected || isJewelleryOrTraditionalWatchesSelected || isUnspecifiedLaptopSelected || isOtherUnspecifiedItemsSelected) && claimTypesInit !== undefined) {
      const baggageAndPersonalItemsCode = stepBaggageAndPersonalItemsSCTILookup.baggageAndPersonalItems.claimCode;
      const daggageDelayCode = stepBaggageAndPersonalItemsSCTILookup.daggageDelay.claimCode;
      const essentialMedicationCode = stepBaggageAndPersonalItemsSCTILookup.essentialMedication.claimCode;
      const specifiedHighValueItemsCode = stepBaggageAndPersonalItemsSCTILookup.specifiedHighValueItems.claimCode;
      const bankCardsOrTravelDocumentsCode = stepBaggageAndPersonalItemsSCTILookup.bankCardsOrTravelDocuments.claimCode;

      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === baggageAndPersonalItemsCode && (isDaggageDelaySelected || isEssentialMedicationSelected
            || isSpecifiedHighValueItemsSelected || isJewelleryOrTraditionalWatchesSelected || isUnspecifiedLaptopSelected
            || isOtherUnspecifiedItemsSelected || ((isBankCardsOrTravelDocumentsSelected || isCashSelected) && isShowParentDocuments))) {
            const benefit = this.mapQuestionItem(item, 'Baggage and Personal Items');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === daggageDelayCode && isDaggageDelaySelected) {
            const benefit = this.mapQuestionItem(item, 'Baggage delay');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === essentialMedicationCode && isEssentialMedicationSelected) {
            const benefit = this.mapQuestionItem(item, 'Essential medication');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === specifiedHighValueItemsCode && isSpecifiedHighValueItemsSelected) {
            const benefit = this.mapQuestionItem(item, 'Specified high value items');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === bankCardsOrTravelDocumentsCode && (isBankCardsOrTravelDocumentsSelected || isCashSelected)) {
            const benefit = this.mapQuestionItem(item, 'Cash, bank cards, travel documents and passports');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    // Changes to your journey SCTI
    const didYouAlsoIncurTheseCostsAsAResultOfYourTravelDisruptionSelected = !!didYouAlsoIncurTheseCostsAsAResultOfYourTravelDisruption && typeof didYouAlsoIncurTheseCostsAsAResultOfYourTravelDisruption.toJS === 'function'
      ? didYouAlsoIncurTheseCostsAsAResultOfYourTravelDisruption.toJS()
      : didYouAlsoIncurTheseCostsAsAResultOfYourTravelDisruption || [];
    const isChildCareExpensesSelected = didYouAlsoIncurTheseCostsAsAResultOfYourTravelDisruptionSelected.includes('Childcare expenses');
    const isPetBoardingFacilitySelected = didYouAlsoIncurTheseCostsAsAResultOfYourTravelDisruptionSelected.includes('Pet boarding facility');
    if ((haveYouCanceledOrChangedYourJourneyBeforeLeaving === 'true' || wasTheUnexpectedEventRelatedToTheRelevantPersonsExistingHealthCondition !== '' || wereYouChargedACancellationFeeByTheTravelAgentPI === 'true' || wereYouChargedACancellationFeeByTheTravelAgent === 'true' || wereYouDelayedWhileTravelingToASpecialEvent === 'true'
      || haveYouChangedYourJourneyAfterLeaving === 'true' || wereYouDelayedWhileTravelingToASpecialEventAL === 'true' || isChildCareExpensesSelected || isPetBoardingFacilitySelected
      || wasTheUnexpectedEventRelatedToTheRelevantPersonsExistingHealthConditionAL === 'true') && claimTypesInit !== undefined) {
      // Cancelling or changing your journey before you leave
      const cancellingOrChangingYourJourneyBLCode = stepChangesToYourJourneyLookup.haveYouCanceledOrChangedYourJourneyBeforeLeaving.claimCode;
      const existingConditionOfARelevantPersonCode = stepChangesToYourJourneyLookup.wasTheUnexpectedEventRelatedToTheRelevantPersonsExistingHealthConditionYes.claimCode;
      const travelAgentCancellationFeeCode = stepChangesToYourJourneyLookup.wereYouChargedACancellationFeeByTheTravelAgent.claimCode;
      const travelAgentCancellationFeeCodePI = stepChangesToYourJourneyLookup.wereYouChargedACancellationFeeByTheTravelAgent.claimCode;
      const delayedJourneyToASpecialEventCode = stepChangesToYourJourneyLookup.wereYouDelayedWhileTravelingToASpecialEvent.claimCode;
      // Changes to your journey once you have left
      const cancellingOrChangingYourJourneyALCode = stepChangesToYourJourneyLookup.haveYouChangedYourJourneyAfterLeaving.claimCode;
      const delayedJourneyToASpecialEventALCode = stepChangesToYourJourneyLookup.wereYouDelayedWhileTravelingToASpecialEventAL.claimCode;
      const childCareExpensesCode = stepChangesToYourJourneyLookup.childCareExpenses.claimCode;
      const petBoardingFacilityCode = stepChangesToYourJourneyLookup.petBoardingFacility.claimCode;
      const existingConditionOfARelevantPersonALCode = stepChangesToYourJourneyLookup.wasTheUnexpectedEventRelatedToTheRelevantPersonsExistingHealthConditionAL.claimCode;
      claimTypesInit.forEach(
        (item) => {
          // Cancelling or changing your journey before you leave
          if (item.claimCode === cancellingOrChangingYourJourneyBLCode && haveYouCanceledOrChangedYourJourneyBeforeLeaving === 'true') {
            const benefit = this.mapQuestionItem(item, 'Cancelling or changing your journey before you leave');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === existingConditionOfARelevantPersonCode && wasTheUnexpectedEventRelatedToTheRelevantPersonsExistingHealthCondition === 'true') {
            const benefit = this.mapQuestionItem(item, 'Existing condition of a relevant person');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          // if (item.claimCode === travelAgentCancellationFeeCodePI && wereYouChargedACancellationFeeByTheTravelAgentPI === 'true') {
          //   const benefit = this.mapQuestionItem(item, 'Travel Agent Cancellation fee');
          //   if (!!benefit) {
          //     list.push(benefit);
          //   }
          // }
          if (item.claimCode === travelAgentCancellationFeeCode && wereYouChargedACancellationFeeByTheTravelAgent === 'true') {
            const benefit = this.mapQuestionItem(item, 'Travel Agent Cancellation fee');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === delayedJourneyToASpecialEventCode && wereYouDelayedWhileTravelingToASpecialEvent === 'true') {
            const benefit = this.mapQuestionItem(item, 'Delayed journey to a special event');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          // Changes to your journey once you have left
          if (item.claimCode === cancellingOrChangingYourJourneyALCode && haveYouChangedYourJourneyAfterLeaving === 'true') {
            const benefit = this.mapQuestionItem(item, 'Changes to your journey once you have left');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === delayedJourneyToASpecialEventALCode && wereYouDelayedWhileTravelingToASpecialEventAL === 'true') {
            const benefit = this.mapQuestionItem(item, 'Delayed journey to a special event');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === existingConditionOfARelevantPersonALCode && wasTheUnexpectedEventRelatedToTheRelevantPersonsExistingHealthConditionAL === 'true') {
            const benefit = this.mapQuestionItem(item, 'Existing condition of a relevant person');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === childCareExpensesCode && isChildCareExpensesSelected) {
            const benefit = this.mapQuestionItem(item, 'Childcare expenses');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === petBoardingFacilityCode && isPetBoardingFacilitySelected) {
            const benefit = this.mapQuestionItem(item, 'Pet services');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    // Bank Cards or travel documents and Cash SCTI
    const cashBankCardsTravelDocumentsAndPassportsSelected = !!passportItemsClaiming && typeof passportItemsClaiming.toJS === 'function'
      ? passportItemsClaiming.toJS()
      : passportItemsClaiming || [];
    const isPassportsBankCardsOrTravelDocumentsSelected = cashBankCardsTravelDocumentsAndPassportsSelected.includes('Bank cards or travel documents');
    const isPassportsCashSelected = cashBankCardsTravelDocumentsAndPassportsSelected.includes('Cash');

    if ((isPassportsBankCardsOrTravelDocumentsSelected || isPassportsCashSelected) && claimTypesInit !== undefined) {
      const baggageAndPersonalItemsCode = stepCashBankCardsTravelDocumentsAndPassportsLookup.baggageAndPersonalItems.claimCode;
      const bankCardsOrTravelDocumentsCode = stepCashBankCardsTravelDocumentsAndPassportsLookup.bankCardsOrTravelDocuments.claimCode;
      const cashCode = stepCashBankCardsTravelDocumentsAndPassportsLookup.cash.claimCode;

      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === bankCardsOrTravelDocumentsCode && (isPassportsBankCardsOrTravelDocumentsSelected || isPassportsCashSelected)) {
            const benefit = this.mapQuestionItem(item, 'Cash, bank cards, travel documents and passports');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    // Medical Expenses Tugo
    const {
      whatExpensesAreYouClaimingFor: whatExpensesAreYouClaimingForMedicalExpensesTugo,
      isClaimRelatedToSelfInflictedYes,
      isClaimRelatedToSelfInflictedNo,
      isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt,
      isYourClaimRelatedToPsychiatricTreatment,
      isYourClaimRelatedToHIV,
      diagnosticImaging
    } = this.props.stepMedicalExpensesTugoData;

    const medicalExpensesTugoSelected = !!whatExpensesAreYouClaimingForMedicalExpensesTugo && typeof whatExpensesAreYouClaimingForMedicalExpensesTugo.toJS === 'function'
      ? whatExpensesAreYouClaimingForMedicalExpensesTugo.toJS()
      : whatExpensesAreYouClaimingForMedicalExpensesTugo || [];

    const isOutpatientDoctorOrClinicVisitSelected = medicalExpensesTugoSelected.includes('Outpatient Doctor or Clinic Visit');
    const isClaimRelatedToSelfInflictedYesChecked = isClaimRelatedToSelfInflictedYes === 'true' || isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttempt === 'yes';
    const isClaimRelatedToSelfInflictedNoChecked = isClaimRelatedToSelfInflictedNo === 'true';

    const isDiagnosticImagingSelected = medicalExpensesTugoSelected.includes('Diagnostic Imaging');
    const isNonMRIChecked = diagnosticImaging.includes('diagnosticNonMRI');
    const isMRIChecked = diagnosticImaging.includes('diagnosticMRI');

    const isMaternityExpenseSelected = medicalExpensesTugoSelected.includes('Maternity Expense');
    const isOncologySelected = medicalExpensesTugoSelected.includes('Oncology');
    const isCleftLipAndPalateAssistanceSelected = medicalExpensesTugoSelected.includes('Cleft Lip and Palate Assistance');
    const isWellBabyCareSelected = medicalExpensesTugoSelected.includes('Well Baby Care');
    const isAnnualHealthExamSelected = medicalExpensesTugoSelected.includes('Annual Health Exam');
    const isVaccinationExpensesSelected = medicalExpensesTugoSelected.includes('Vaccination Expenses');
    const isbloodPlasmaOrWholeBloodTransfusionSelected = medicalExpensesTugoSelected.includes('Blood plasma or whole blood (transfusion)');
    const isAnaesthesiologySelected = medicalExpensesTugoSelected.includes('Anaesthesiology');
    const isDentalSurgerySelected = medicalExpensesTugoSelected.includes('Dental Surgery');

    const isYourClaimRelatedToPsychiatricTreatmentChecked = isYourClaimRelatedToPsychiatricTreatment === 'yes';
    const isYourClaimRelatedToHIVChecked = isYourClaimRelatedToHIV === 'yes';
    const isHospitalizationChargesChecked = isYourClaimRelatedToHIV === 'no';


    if (medicalExpensesTugoSelected.length > 0 && claimTypesInit !== undefined) {
      const outpatientDoctorOrClinicVisitCode = stepMedicalExpensesTugoLookup.outpatientDoctorOrClinicVisit.claimCode;
      const isClaimRelatedToSelfInflictedCode = stepMedicalExpensesTugoLookup.isYourClaimRelatedToASelfInflictedInjuryOrSuicideAttemptYes.claimCode;

      const diagnosticMRICode = stepMedicalExpensesTugoLookup.diagnosticMRI.claimCode;
      const diagnosticNonMRICode = stepMedicalExpensesTugoLookup.diagnosticNonMRI.claimCode;

      const maternityExpenseCode = stepMedicalExpensesTugoLookup.maternityExpense.claimCode;
      const oncologyCode = stepMedicalExpensesTugoLookup.oncology.claimCode;
      const cleftLipAndPalateAssistanceCode = stepMedicalExpensesTugoLookup.cleftLipAndPalateAssistance.claimCode;
      const wellBabyCareCode = stepMedicalExpensesTugoLookup.wellBabyCare.claimCode;
      const annualHealthExamCode = stepMedicalExpensesTugoLookup.annualHealthExam.claimCode;
      const vaccinationExpensesCode = stepMedicalExpensesTugoLookup.vaccinationExpenses.claimCode;
      const psychiatricTreatmentCode = stepMedicalExpensesTugoLookup.isYourClaimRelatedToPsychiatricTreatment.claimCode;
      const HIVCode = stepMedicalExpensesTugoLookup.isYourClaimRelatedToHIV.claimCode;
      const bloodPlasmaOrWholeBloodTransfusionCode = stepMedicalExpensesTugoLookup.bloodPlasmaOrWholeBloodTransfusion.claimCode;
      const anaesthesiologyCode = stepMedicalExpensesTugoLookup.anaesthesiology.claimCode;
      const dentalSurgeryCode = stepMedicalExpensesTugoLookup.dentalSurgery.claimCode;


      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === outpatientDoctorOrClinicVisitCode && isClaimRelatedToSelfInflictedNoChecked) {
            const benefit = this.mapQuestionItem(item, 'Outpatient Doctor or Clinic Visit');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === isClaimRelatedToSelfInflictedCode && isClaimRelatedToSelfInflictedYesChecked) {
            const benefit = this.mapQuestionItem(item, 'Self Inflicted Injuries and Attempted Suicide');
            if (!!benefit) {
              list.push(benefit);
            }
          }

          if (item.claimCode === diagnosticMRICode && isMRIChecked) {
            const benefit = this.mapQuestionItem(item, 'MRI Scans');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === diagnosticNonMRICode && isNonMRIChecked) {
            const benefit = this.mapQuestionItem(item, 'Standard X-Ray and Laboratory Examinations');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === maternityExpenseCode && isMaternityExpenseSelected) {
            const benefit = this.mapQuestionItem(item, 'Maternity Expense');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === oncologyCode && isOncologySelected) {
            const benefit = this.mapQuestionItem(item, 'Oncology');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === cleftLipAndPalateAssistanceCode && isCleftLipAndPalateAssistanceSelected) {
            const benefit = this.mapQuestionItem(item, 'Cleft Lip and Palate Assistance');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === wellBabyCareCode && isWellBabyCareSelected) {
            const benefit = this.mapQuestionItem(item, 'Well Baby Care');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === annualHealthExamCode && isAnnualHealthExamSelected) {
            const benefit = this.mapQuestionItem(item, 'Annual Health Exam');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === vaccinationExpensesCode && isVaccinationExpensesSelected) {
            const benefit = this.mapQuestionItem(item, 'Vaccination Expenses');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === psychiatricTreatmentCode && isYourClaimRelatedToPsychiatricTreatmentChecked) {
            const benefit = this.mapQuestionItem(item, 'Hospital Charges Related to Psychiatric Treatment');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === HIVCode && isYourClaimRelatedToHIVChecked) {
            const benefit = this.mapQuestionItem(item, 'Hospital Charges Related to HIV');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === HIVCode && isHospitalizationChargesChecked) {
            const benefit = this.mapQuestionItem(item, 'Hospitalization Charges');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === bloodPlasmaOrWholeBloodTransfusionCode && isbloodPlasmaOrWholeBloodTransfusionSelected) {
            const benefit = this.mapQuestionItem(item, 'Blood plasma or whole blood (transfusion)');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === anaesthesiologyCode && isAnaesthesiologySelected) {
            const benefit = this.mapQuestionItem(item, 'Anaesthesiology');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === dentalSurgeryCode && isDentalSurgerySelected) {
            const benefit = this.mapQuestionItem(item, 'Dental Surgery');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    // Other Expenses Tugo
    const {
      whatExpensesAreYouClaimingFor: whatExpensesAreYouClaimingForOtherTugo,
    } = this.props.stepOtherTugoData;
    const otherTugoSelected = !!whatExpensesAreYouClaimingForOtherTugo && typeof whatExpensesAreYouClaimingForOtherTugo.toJS === 'function'
      ? whatExpensesAreYouClaimingForOtherTugo.toJS()
      : whatExpensesAreYouClaimingForOtherTugo || [];
    const isRepatriationBenefitSelected = otherTugoSelected.includes('Repatriation Benefit (Return of Remains)');
    const isRepatriationSelected = otherTugoSelected.includes('Repatriation (Burial at place of death)');
    const isReturnHomeBenefitSelected = otherTugoSelected.includes('Return Home Benefit');

    if (otherTugoSelected.length > 0 && claimTypesInit !== undefined) {
      const repatriationBenefitCode = stepOtherTugoLookup.repatriationBenefit.claimCode;
      const repatriationCode = stepOtherTugoLookup.repatriation.claimCode;
      const returnHomeBenefitCode = stepOtherTugoLookup.returnHomeBenefit.claimCode;

      claimTypesInit.forEach(
        (item) => {
          if (item.claimCode === repatriationBenefitCode && isRepatriationBenefitSelected) {
            const benefit = this.mapQuestionItem(item, 'Repatriation Benefit (Return of Remains)');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === repatriationCode && isRepatriationSelected) {
            const benefit = this.mapQuestionItem(item, 'Repatriation (Burial at place of death)');
            if (!!benefit) {
              list.push(benefit);
            }
          }
          if (item.claimCode === returnHomeBenefitCode && isReturnHomeBenefitSelected) {
            const benefit = this.mapQuestionItem(item, 'Return Home Benefit');
            if (!!benefit) {
              list.push(benefit);
            }
          }
        }
      );
    }

    // Tugo Benefits
    const selectedBenefitsBaseVictorsMedical = stepBaseVictorsMedicalTugoDataMapper.mapToClaimExpensesLevel(this.props.stepBaseVictorsMedicalTugoData, claimTypesInit, selectedBenefits);
    // Medical Expenses Tugo
    if (selectedBenefitsBaseVictorsMedical.length > 0) {
      list.push(...selectedBenefitsBaseVictorsMedical);
    }

    return list;
  }

  mapQuestionItem(claimType, description) {
    return {
      claimCode: claimType.claimCode,
      description,
      claimTypeId: claimType.claimTypeId,
      requiredDocuments: claimType.requiredDocuments,
    };
  }

  renderThirdPartyAuthority() {
    const {
      givePermission,
      thirdPartyTitle,
      thirdPartyFirstName,
      thirdPartySurName,
      thirdPartyRelationShip,
      thirdPartyAddressLine1,
      thirdPartyAddressLine2,
      thirdPartyCity,
      thirdPartyCountry,
      thirdPartyState,
      thirdPartyPostal,
      thirdPartyMobilePhone,
      thirdPartyEmail,
    } = this.props.isSummary ? this.summaryFormValue() : this.props.form;
    const { isSummary, submitted, jsonVersion, intl } = this.props;
    const displayClaimForField = jsonVersion >= 6;
    const isReadOnly = submitted || isSummary;
    // Validate
    const valueThirdPartyFirstName = thirdPartyFirstName ? thirdPartyFirstName.toString() : '';
    const valueThirdPartySurName = thirdPartySurName ? thirdPartySurName.toString() : '';
    const valueThirdPartyEmail = thirdPartyEmail ? thirdPartyEmail.toString() : '';
    const valueAddressLine1 = thirdPartyAddressLine1 ? thirdPartyAddressLine1.toString() : '';
    const valueThirdPartyMobilePhone = thirdPartyMobilePhone ? thirdPartyMobilePhone.toString() : '';
    const valueAddressLine2 = thirdPartyAddressLine2 ? thirdPartyAddressLine2.toString() : '';
    const valuePartyCountry = thirdPartyCountry ? thirdPartyCountry.toString() : '';
    const valuePartyState = thirdPartyState ? thirdPartyState.toString() : '';
    const valuePartyPostal = thirdPartyPostal ? thirdPartyPostal.toString() : '';
    const valuePartyRelationShip = thirdPartyRelationShip ? thirdPartyRelationShip.toString() : '';
    const isValidateThirdPartyFirstName = valueThirdPartyFirstName.toString().trim().length > 128;
    const isValidateThirdPartySurName = valueThirdPartySurName.toString().trim().length > 128;
    const isValidateThirdPartyEmail = valueThirdPartyEmail.toString().trim().length > 256;
    const isValidateMobilePhone = valueThirdPartyMobilePhone.toString().trim().length > 32;
    const isValidateAddressLine1 = valueAddressLine1.toString().trim().length > 100;
    const isValidateAddressLine2 = valueAddressLine2.toString().trim().length > 100;
    const isValidatePartyCountry = valuePartyCountry.toString().trim().length > 32;
    const isValidatePartyState = valuePartyState.toString().trim().length > 32;
    const isValidatePartyPostal = valuePartyPostal.toString().trim().length > 32;
    const isValidatePartyRelationShip = valuePartyRelationShip.toString().trim().length > 100;
    const invalidEmail = this.isValidEmail(valueThirdPartyEmail);
    return (
      givePermission === 'true' && (
        <div className={'ml-4'}>
          <div className={'row'}>
            <div className={'col-sm-4 form-group'}>
              {/* <PageSectionHeader> */}
              {/* <FormattedMessage {...messages.title} /> */}
              {/* </PageSectionHeader> */}
              <Validate
                name="thirdPartyTitle"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                defaultValue={thirdPartyTitle}
                fieldName={messages.title}
              >
                <Select
                  name="thirdPartyTitle"
                  onChange={this.handleChange('form.thirdPartyTitle')}
                  disabled={isReadOnly}
                  value={thirdPartyTitle}
                >
                  <option value="" />
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Mstr">Mstr</option>
                  <option value="Miss">Miss</option>
                </Select>
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.title' }) }}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className={'col-sm-4 form-group'}>
              <Validate
                name="thirdPartyFirstName"
                validators={[required, maxLength(128)]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                defaultValue={thirdPartyFirstName}
                fieldName={messages.firstName}
              >
                <Textbox
                  name="thirdPartyFirstName"
                  type="text"
                  onChange={this.handleChange('form.thirdPartyFirstName')}
                  value={thirdPartyFirstName}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.firstName' }) }}
                  />
                </ErrorMessage>
              </Validate>
              {isValidateThirdPartyFirstName &&
                <P style={{ color: 'red', fontSize: '0.7em' }}>
                  <FormattedMessage
                    {...messages.isMaxLength}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.firstName' }) }}
                  />
                </P>
              }
            </div>
            <div className={'col-sm-4 form-group'}>
              <Validate
                name="thirdPartySurName"
                validators={[required, maxLength(128)]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                defaultValue={thirdPartySurName}
                fieldName={messages.surname}
              >
                <Textbox
                  name="thirdPartySurName"
                  type="text"
                  onChange={this.handleChange('form.thirdPartySurName')}
                  value={thirdPartySurName}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.surname' }) }}
                  />
                </ErrorMessage>
              </Validate>
              {isValidateThirdPartySurName &&
                <P style={{ color: 'red', fontSize: '0.7em' }}>
                  <FormattedMessage
                    {...messages.isMaxLength}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.surname' }) }}
                  />
                </P>
              }
            </div>
          </div>
          <div className={'form-group'}>
            <Validate
              name="thirdPartyAddressLine1"
              validators={[required, maxLength(100)]}
              group={this.validatorGroup}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              defaultValue={thirdPartyAddressLine1}
              fieldName={messages.address}
            >
              <Textbox
                id="thirdPartyAddressLine1"
                name="thirdPartyAddressLine1"
                type="text"
                placeholder={intl.formatMessage({ id: 'fastCover.stepClaimSummary.addressLine1' })}
                onChange={this.handleChange('form.thirdPartyAddressLine1')}
                value={thirdPartyAddressLine1}
                disabled={isReadOnly}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.address' }) }}
                />
              </ErrorMessage>
            </Validate>
            {isValidateAddressLine1 &&
              <P style={{ color: 'red', fontSize: '0.7em' }}>
                <FormattedMessage
                  {...messages.isMaxLength}
                  values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.address' }) }}
                />
              </P>
            }
          </div>
          <div className={'form-group'}>
            <Validate
              name="thirdPartyAddressLine2"
              validators={[maxLength(100)]}
              group={this.validatorGroup}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              defaultValue={thirdPartyAddressLine2}
            >
              <Textbox
                name="thirdPartyAddressLine2"
                type="text"
                placeholder={intl.formatMessage({ id: 'fastCover.stepClaimSummary.addressLine2' })}
                onChange={this.handleChange('form.thirdPartyAddressLine2')}
                value={thirdPartyAddressLine2}
                disabled={isReadOnly}
              />
              <ErrorMessage >
              </ErrorMessage>
            </Validate>
            {isValidateAddressLine2 &&
              <P style={{ color: 'red', fontSize: '0.7em' }}>
                <FormattedMessage
                  {...messages.isMaxLength}
                  values={messages.addressLine2}
                />
              </P>
            }
          </div>
          <div className={'row'}>
            {displayClaimForField &&
              <div className={'col-sm-4 form-group'}>
                <Validate
                  name="thirdPartyCountry"
                  validators={[required, maxLength(32)]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={thirdPartyCountry}
                >
                  <Textbox
                    name="thirdPartyCountry"
                    type="text"
                    placeholder={intl.formatMessage({ id: 'fastCover.stepClaimSummary.country' })}
                    onChange={this.handleChange('form.thirdPartyCountry')}
                    value={thirdPartyCountry}
                    disabled={isReadOnly}
                  />
                  <ErrorMessage for="required">
                    <FormattedMessage
                      {...messages.isRequired}
                      values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.country' }) }}
                    />
                  </ErrorMessage>
                </Validate>
                {isValidatePartyCountry &&
                  <P style={{ color: 'red', fontSize: '0.7em' }}>
                    <FormattedMessage
                      {...messages.isMaxLength}
                      values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.country' }) }}
                    />
                  </P>
                }
              </div>}
            {!displayClaimForField &&
              <div className={'col-sm-4 form-group'}>
                <Textbox
                  name="thirdPartyCountry"
                  type="text"
                  placeholder={intl.formatMessage({ id: 'fastCover.stepClaimSummary.country' })}
                  onChange={this.handleChange('form.thirdPartyCountry')}
                  value={thirdPartyCountry}
                  disabled={isReadOnly}
                />
              </div>}
            <div className={'col-sm-4 form-group'}>
              <Validate
                name="thirdPartyState"
                validators={[required, maxLength(32)]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                defaultValue={thirdPartyState}
              >
                <Textbox
                  name="thirdPartyState"
                  type="text"
                  placeholder={intl.formatMessage({ id: 'fastCover.stepClaimSummary.stateProvinceRegion' })}
                  onChange={this.handleChange('form.thirdPartyState')}
                  value={thirdPartyState}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.stateProvinceRegion' }) }}
                  />
                </ErrorMessage>
              </Validate>
              {isValidatePartyState &&
                <P style={{ color: 'red', fontSize: '0.7em' }}>
                  <FormattedMessage
                    {...messages.isMaxLength}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.stateProvinceRegion' }) }}
                  />
                </P>
              }
            </div>
            <div className={'col-sm-4 form-group'}>
              <Validate
                name="thirdPartyPostal"
                validators={[required, maxLength(32)]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                defaultValue={thirdPartyPostal}
              >
                <Textbox
                  name="thirdPartyPostal"
                  type="text"
                  placeholder={intl.formatMessage({ id: 'fastCover.stepClaimSummary.postalZipCode' })}
                  onChange={this.handleChange('form.thirdPartyPostal')}
                  value={thirdPartyPostal}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.postalZipCode' }) }}
                  />
                </ErrorMessage>
              </Validate>
              {isValidatePartyPostal &&
                <P style={{ color: 'red', fontSize: '0.7em' }}>
                  <FormattedMessage
                    {...messages.isMaxLength}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.postalZipCode' }) }}
                  />
                </P>
              }
            </div>
          </div>

          <div className={'row'}>
            <div className={'col-sm-4 form-group'}>
              {/* <PageSectionHeader> */}
              {/* <FormattedMessage {...messages.mobile} /> */}
              {/* </PageSectionHeader> */}
              <Validate
                name="thirdPartyMobilePhone"
                validators={[required, maxLength(32)]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                defaultValue={thirdPartyMobilePhone}
                fieldName={messages.mobile}
              >
                <Textbox
                  name="thirdPartyMobilePhone"
                  type="text"
                  onChange={this.handleChangePhoneNumber('form.thirdPartyMobilePhone')}
                  value={thirdPartyMobilePhone}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.mobile' }) }}
                  />
                </ErrorMessage>
              </Validate>
              {isValidateMobilePhone &&
                <P style={{ color: 'red', fontSize: '0.7em' }}>
                  <FormattedMessage
                    {...messages.isMaxLength}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.mobile' }) }}
                  />
                </P>
              }
            </div>
            <div className={'col-sm-4 form-group'}>
              <Validate
                name="thirdPartyEmail"
                validators={[required, maxLength(256), validEmail]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                defaultValue={thirdPartyEmail}
                fieldName={messages.email}
              >
                <Textbox
                  name="thirdPartyEmail"
                  type="text"
                  onChange={this.handleChange('form.thirdPartyEmail')}
                  value={thirdPartyEmail}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.email' }) }}
                  />
                </ErrorMessage>
              </Validate>
              {!invalidEmail &&
                <P style={{ color: 'red', fontSize: '0.7em' }}>
                  <FormattedMessage
                    {...messages.isValid}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.email' }) }}
                  />
                </P>
              }
              {isValidateThirdPartyEmail &&
                <P style={{ color: 'red', fontSize: '0.7em' }}>
                  <FormattedMessage
                    {...messages.isMaxLength}
                    values={messages.email}
                  />
                </P>
              }
            </div>
            <div className={'col-sm-4 form-group'}>
              <Validate
                name="thirdPartyRelationShip"
                validators={[required, maxLength(100)]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                defaultValue={thirdPartyRelationShip}
                fieldName={messages.relationshipToYou}
              >
                <Textbox
                  name="thirdPartyRelationShip"
                  type="text"
                  onChange={this.handleChange('form.thirdPartyRelationShip')}
                  value={thirdPartyRelationShip}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.relationshipToYou' }) }}
                  />
                </ErrorMessage>
              </Validate>
              {isValidatePartyRelationShip &&
                <P style={{ color: 'red', fontSize: '0.7em' }}>
                  <FormattedMessage
                    {...messages.isMaxLength}
                    values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.relationshipToYou' }) }}
                  />
                </P>
              }
            </div>
          </div>
        </div>
      )
    );
  }

  render() {
    const { isSummary, submitted, loading, isClaimGroupAllowed, isClaimForFieldEnabled, intl, hasFormChanges } = this.props;
    const {
      givePermission,
      preferredContactNumberForClaim,
      preferredContactEmailForClaim,
      preferredContactForClaim,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const { listDocuments } = this.state;
    const isReadOnly = submitted || isSummary;
    const { selectedBenefits, selectedClaimFor } = this.props.stepWhatHappenedData;
    const list = (!!selectedBenefits && typeof selectedBenefits.toJS === 'function') ? selectedBenefits.toJS() : (selectedBenefits || []);
    const isVisibleDuesProtect = (list.filter((d) => ((listCodeForDuesProtect).filter((c) => c === d.claimCode)).length > 0)).length > 0;
    const valueContact = preferredContactForClaim ? preferredContactForClaim.toString() : '';
    const valueContactNumber = preferredContactNumberForClaim ? preferredContactNumberForClaim.toString() : '';
    const valueContactEmail = preferredContactEmailForClaim ? preferredContactEmailForClaim.toString() : '';
    const isValidateContactNumber = valueContactNumber.toString().trim().length > 32;
    const isValidateContactEmail = valueContactEmail.toString().trim().length > 256;
    const isValidateContact = valueContact.toString().trim().length > 50;
    const invalidContactEmail = this.isValidEmail(valueContactEmail);
    const selectedClaimForList = selectedClaimFor ? selectedClaimFor.toJS ? selectedClaimFor.toJS() : selectedClaimFor : [];
    const isSelectedMultipleClaimFor = (isClaimForFieldEnabled && selectedClaimForList.length > 1) || (selectedClaimForList.length > 1 && isClaimGroupAllowed);
    return (
      <div className="app-content">
        <H3><FormattedMessage {...messages.summary} id={messages.summary.id} /></H3>
        {isVisibleDuesProtect === true &&
          <Section className="table-responsive-sm pt-0">
            <DocumentTableDuesProtect
              stepWhatHappenedData={this.props.stepWhatHappenedData}
              stepSupportingDocumentsData={this.props.stepSupportingDocumentsData}
              stepMedicalHardshipData={this.props.stepMedicalHardshipData}
              stepJobLossHardshipData={this.props.stepJobLossHardshipData}
              stepDisasterAssistanceHardshipData={this.props.stepDisasterAssistanceHardshipData}
              userInfo={this.props.userInfo}
              claimTypes={this.props.claimTypes}
            />
          </Section>}
        {isVisibleDuesProtect === false &&
          <Section className="table-responsive-sm pt-0">
            {isSelectedMultipleClaimFor &&
              <DocumentTableMultiInsureds
                stepWhatHappenedData={this.props.stepWhatHappenedData}
                stepOverseasMedicalData={this.props.stepOverseasMedicalData}
                stepSupportingDocumentsData={this.props.stepSupportingDocumentsData}
                stepPersonalItemsData={this.props.stepPersonalItemsData}
                stepCancellationAdditionalData={this.props.stepCancellationAdditionalData}
                stepOtherData={this.props.stepOtherData}
                stepRentalVehicleData={this.props.stepRentalVehicleData}
                stepTripCancellationAndTripInterruptionData={this.props.stepTripCancellationAndTripInterruptionData}
                stepBaggageAndPersonalItemsData={this.props.stepBaggageAndPersonalItemsData}
                stepMedicalExpensesData={this.props.stepMedicalExpensesData}
                stepPaymentDetailsData={this.props.stepPaymentDetailsData}
                stepBankDetailsData={this.props.stepBankDetailsData}
                stepMedicalExpenseData={this.props.stepMedicalExpenseData}
                stepTripCancellationData={this.props.stepTripCancellationData}
                stepTripDelayData={this.props.stepTripDelayData}
                stepTripInterruptionData={this.props.stepTripInterruptionData}
                stepPetMedicalData={this.props.stepPetMedicalData}
                stepPropertyDamageData={this.props.stepPropertyDamageData}
                stepCollisionDamageData={this.props.stepCollisionDamageData}
                stepMissedConnectionData={this.props.stepMissedConnectionData}
                stepSportsEquipmentData={this.props.stepSportsEquipmentData}
                stepPersonalItemData={this.props.stepPersonalItemData}
                stepMedicalExpenseCFData={this.props.stepMedicalExpenseCFData}
                stepMedicalExpenseZurichData={this.props.stepMedicalExpenseZurichData}
                stepItineraryChangeData={this.props.stepItineraryChangeData}
                stepBaggagePersonalEffectsData={this.props.stepBaggagePersonalEffectsData}
                stepTravelDelayData={this.props.stepTravelDelayData}
                showZurichUHC={this.props.showZurichUHC}
                claimantId={this.props.claimantId}
                insuredsList={this.props.insuredsList}
                stepMedicalAndEvacuationSCTIData={this.props.stepMedicalAndEvacuationSCTIData}
                stepChangesToYourJourneySCTIData={this.props.stepChangesToYourJourneySCTIData}
                stepBaggageAndPersonalItemsSCTIData={this.props.stepBaggageAndPersonalItemsSCTIData}
                stepCashBankCardsTravelDocumentsAndPassportsData={this.props.stepCashBankCardsTravelDocumentsAndPassportsData}
                stepPersonalAccidentSCTIData={this.props.stepPersonalAccidentSCTIData}
                stepRentalVehicleExcessSCTIData={this.props.stepRentalVehicleExcessSCTIData}
                stepChangesToYourJourneyData={this.props.stepChangesToYourJourneyData}
                stepBaggageAndPersonalItemsStarrData={this.props.stepBaggageAndPersonalItemsStarrData}
                stepOtherStarrData={this.props.stepOtherStarrData}
                stepCollisionDamageStarrData={this.props.stepCollisionDamageStarrData}
                listDocuments={listDocuments}
                stepMedicalExpensesTugoData={this.props.stepMedicalExpensesTugoData}
                stepOtherTugoData={this.props.stepOtherTugoData}
                stepBaseVictorsMedicalTugoData={this.props.stepBaseVictorsMedicalTugoData}
                exchangeRates={this.props.exchangeRates}
                exchangeRatesLoading={this.props.exchangeRatesLoading}
              />
            }
            {!isSelectedMultipleClaimFor &&
              <DocumentTable
                stepWhatHappenedData={this.props.stepWhatHappenedData}
                stepOverseasMedicalData={this.props.stepOverseasMedicalData}
                stepSupportingDocumentsData={this.props.stepSupportingDocumentsData}
                stepPersonalItemsData={this.props.stepPersonalItemsData}
                stepCancellationAdditionalData={this.props.stepCancellationAdditionalData}
                stepOtherData={this.props.stepOtherData}
                stepRentalVehicleData={this.props.stepRentalVehicleData}
                stepTripCancellationAndTripInterruptionData={this.props.stepTripCancellationAndTripInterruptionData}
                stepBaggageAndPersonalItemsData={this.props.stepBaggageAndPersonalItemsData}
                stepMedicalExpensesData={this.props.stepMedicalExpensesData}
                stepPaymentDetailsData={this.props.stepPaymentDetailsData}
                stepBankDetailsData={this.props.stepBankDetailsData}
                stepMedicalExpenseData={this.props.stepMedicalExpenseData}
                stepTripCancellationData={this.props.stepTripCancellationData}
                stepTripDelayData={this.props.stepTripDelayData}
                stepTripInterruptionData={this.props.stepTripInterruptionData}
                stepPetMedicalData={this.props.stepPetMedicalData}
                stepPropertyDamageData={this.props.stepPropertyDamageData}
                stepCollisionDamageData={this.props.stepCollisionDamageData}
                stepMissedConnectionData={this.props.stepMissedConnectionData}
                stepSportsEquipmentData={this.props.stepSportsEquipmentData}
                stepPersonalItemData={this.props.stepPersonalItemData}
                stepMedicalExpenseCFData={this.props.stepMedicalExpenseCFData}
                stepMedicalExpenseZurichData={this.props.stepMedicalExpenseZurichData}
                stepItineraryChangeData={this.props.stepItineraryChangeData}
                stepBaggagePersonalEffectsData={this.props.stepBaggagePersonalEffectsData}
                stepTravelDelayData={this.props.stepTravelDelayData}
                showZurichUHC={this.props.showZurichUHC}
                claimantId={this.props.claimantId}
                stepMedicalAndEvacuationSCTIData={this.props.stepMedicalAndEvacuationSCTIData}
                stepChangesToYourJourneySCTIData={this.props.stepChangesToYourJourneySCTIData}
                stepBaggageAndPersonalItemsSCTIData={this.props.stepBaggageAndPersonalItemsSCTIData}
                stepCashBankCardsTravelDocumentsAndPassportsData={this.props.stepCashBankCardsTravelDocumentsAndPassportsData}
                stepPersonalAccidentSCTIData={this.props.stepPersonalAccidentSCTIData}
                stepRentalVehicleExcessSCTIData={this.props.stepRentalVehicleExcessSCTIData}
                stepChangesToYourJourneyData={this.props.stepChangesToYourJourneyData}
                stepBaggageAndPersonalItemsStarrData={this.props.stepBaggageAndPersonalItemsStarrData}
                stepOtherStarrData={this.props.stepOtherStarrData}
                stepCollisionDamageStarrData={this.props.stepCollisionDamageStarrData}
                listDocuments={listDocuments}
                stepMedicalExpensesTugoData={this.props.stepMedicalExpensesTugoData}
                stepOtherTugoData={this.props.stepOtherTugoData}
                stepBaseVictorsMedicalTugoData={this.props.stepBaseVictorsMedicalTugoData}
                exchangeRates={this.props.exchangeRates}
                exchangeRatesLoading={this.props.exchangeRatesLoading}
              />
            }
          </Section>}
        <hr />
        <H3><FormattedMessage {...messages.claimContactInfo} id={messages.claimContactInfo.id} /></H3>
        <div className={'row align-items-end'}>
          <div className={'col-sm-4 form-group'}>
            <Validate
              name="thirdPartyRelationShip"
              validators={[required, maxLength(50)]}
              group={this.validatorGroup}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              defaultValue={preferredContactForClaim}
              fieldName={messages.preferredContactForClaim}
            >
              <Textbox
                name="preferredContactForClaim"
                type="text"
                disabled={isReadOnly}
                onChange={this.handleChange(
                  'form.preferredContactForClaim'
                )}
                value={preferredContactForClaim}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.preferredContactForClaim' }) }}
                />
              </ErrorMessage>
            </Validate>
            {isValidateContact &&
              <P style={{ color: 'red', fontSize: '0.7em' }}>
                <FormattedMessage
                  {...messages.isMaxLength}
                  values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.preferredContactForClaim' }) }}
                />
              </P>
            }
          </div>
          <div className={'col-sm-4 form-group'}>
            <Validate
              name="thirdPartyRelationShip"
              validators={[required, maxLength(32)]}
              group={this.validatorGroup}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              defaultValue={preferredContactNumberForClaim}
              fieldName={messages.preferredContactNumberForClaim}
            >
              <Textbox
                name="preferredContactNumberForClaim"
                type="text"
                disabled={isReadOnly}
                onChange={this.handleContactNumberChange(
                  'form.preferredContactNumberForClaim'
                )}
                value={preferredContactNumberForClaim}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.preferredContactNumberForClaim' }) }}
                />
              </ErrorMessage>
            </Validate>
            {isValidateContactNumber &&
              <P style={{ color: 'red', fontSize: '0.7em' }}>
                <FormattedMessage
                  {...messages.isMaxLength}
                  values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.preferredContactNumberForClaim' }) }}
                />
              </P>
            }
          </div>
          <div className={'col-sm-4 form-group'}>
            <Validate
              name="thirdPartyRelationShip"
              validators={[required, maxLength(256), validEmail]}
              group={this.validatorGroup}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              defaultValue={preferredContactEmailForClaim}
              fieldName={messages.preferredContactEmailForClaim}
            >
              <Textbox
                name="preferredContactEmailForClaim"
                type="text"
                disabled={isReadOnly}
                onChange={this.handleChange('form.preferredContactEmailForClaim')}
                value={preferredContactEmailForClaim}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.preferredContactEmailForClaim' }) }}
                />
              </ErrorMessage>
            </Validate>
            {!invalidContactEmail &&
              <P style={{ color: 'red', fontSize: '0.7em' }}>
                <FormattedMessage
                  {...messages.isValid}
                  values={{ defaultMessage: intl.formatMessage({ id: 'fastCover.stepClaimSummary.email' }) }}
                />
              </P>
            }
            {isValidateContactEmail &&
              <P style={{ color: 'red', fontSize: '0.7em' }}>
                <FormattedMessage
                  {...messages.isMaxLength}
                  values={messages.preferredContactEmailForClaim}
                />
              </P>
            }
          </div>
        </div>
        <PageSectionHeader>
          <FormattedMessage {...messages.wouldYouLikeToGivePermission} id={messages.wouldYouLikeToGivePermission.id} />
        </PageSectionHeader>
        <FlexBox>
          <OptionsContainer>
            <input
              disabled={isReadOnly}
              type="radio"
              value="true"
              checked={givePermission === 'true'}
              onChange={this.handleChange('form.givePermission')}
            />
            <FormattedMessage {...messages.yes} id={messages.yes.id} />
          </OptionsContainer>
          <OptionsContainer>
            <input
              disabled={isReadOnly}
              type="radio"
              value="false"
              checked={givePermission === 'false'}
              onChange={this.handleUncheckedFormState(
                'form.givePermission',
                this.props.onResetGivePermissionState
              )}
            />
            <FormattedMessage {...messages.no} />
          </OptionsContainer>
        </FlexBox>
        {this.renderThirdPartyAuthority()}
        {!isSummary && (
          <div className="mt-3">
            <WizardNavigationButtons
              onBack={this.props.prev}
              onNext={this.handleNext}
              onSave={this.handleSaveButtonClick}
              canMoveNext={true}
              listLength={1}
              submitted={submitted}
              loading={loading}
              saveButtonId={this.saveButtonId}
              showSpinnerForElement={this.showSpinnerForElement}
              handleKeyDown={this.handleKeyDown}
              intl={intl}
              hasFormChanges={hasFormChanges}
              nextButtonRef={(ref) => {
                this.nextButton = ref;
              }}
            />
          </div>
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

StepClaimSummary.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  next: PropTypes.func,
  prev: PropTypes.func,
  onFormDataSave: PropTypes.func,
  onResetGivePermissionState: PropTypes.func,
  adjust: PropTypes.func,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  loading: PropTypes.bool,
  makeError: PropTypes.func,
  makeValid: PropTypes.func,
  step: PropTypes.number,
  stepWhatHappenedData: PropTypes.any,
  stepOverseasMedicalData: PropTypes.any,
  stepPersonalItemsData: PropTypes.any,
  stepSupportingDocumentsData: PropTypes.any,
  stepOtherData: PropTypes.any,
  stepCancellationAdditionalData: PropTypes.any,
  stepRentalVehicleData: PropTypes.any,
  stepTripCancellationAndTripInterruptionData: PropTypes.any,
  stepBaggageAndPersonalItemsData: PropTypes.any,
  stepMedicalExpensesData: PropTypes.any,
  stepPaymentDetailsData: PropTypes.any,
  stepBankDetailsData: PropTypes.any,
  stepMedicalExpenseData: PropTypes.any,
  stepTripCancellationData: PropTypes.any,
  stepTripInterruptionData: PropTypes.any,
  stepTripDelayData: PropTypes.any,
  stepPetMedicalData: PropTypes.any,
  stepPropertyDamageData: PropTypes.any,
  stepCollisionDamageData: PropTypes.any,
  stepMissedConnectionData: PropTypes.any,
  stepSportsEquipmentData: PropTypes.any,
  stepPersonalItemData: PropTypes.any,
  stepMedicalExpenseCFData: PropTypes.any,
  stepMedicalExpenseZurichData: PropTypes.any,
  stepItineraryChangeData: PropTypes.any,
  stepBaggagePersonalEffectsData: PropTypes.any,
  stepMedicalHardshipData: PropTypes.any,
  stepJobLossHardshipData: PropTypes.any,
  stepDisasterAssistanceHardshipData: PropTypes.any,
  stepTravelDelayData: PropTypes.any,
  stepMedicalAndEvacuationSCTIData: PropTypes.any,
  stepChangesToYourJourneySCTIData: PropTypes.any,
  stepBaggageAndPersonalItemsSCTIData: PropTypes.any,
  stepCashBankCardsTravelDocumentsAndPassportsData: PropTypes.any,
  stepPersonalAccidentSCTIData: PropTypes.any,
  stepRentalVehicleExcessSCTIData: PropTypes.any,
  stepChangesToYourJourneyData: PropTypes.any,
  stepBaggageAndPersonalItemsStarrData: PropTypes.any,
  stepOtherStarrData: PropTypes.any,
  stepCollisionDamageStarrData: PropTypes.any,
  stepMedicalExpensesTugoData: PropTypes.any,
  stepOtherTugoData: PropTypes.any,
  stepBaseVictorsMedicalTugoData: PropTypes.any,
  currentStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  validateStep: PropTypes.string,
  stepName: PropTypes.string,
  errors: PropTypes.any,
  isReadOnly: PropTypes.any,
  userInfo: PropTypes.object,
  claimTypes: PropTypes.array,
  jsonVersion: PropTypes.string,
  isClaimGroupAllowed: PropTypes.bool,
  isClaimForFieldEnabled: PropTypes.bool,
  showZurichUHC: PropTypes.bool,
  claimantId: PropTypes.any,
  insuredsList: PropTypes.any,
  claimTypesInit: PropTypes.any,
  policyTypeCode: PropTypes.string,
  intl: intlShape.isRequired,
  hasFormChanges: PropTypes.bool,
  setFormHasChanges: PropTypes.func,
  exchangeRates: PropTypes.object,
  exchangeRatesLoading: PropTypes.bool,
  onFetchExchangeRates: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectStepClaimSummary(),
  dirty: makeSelectStepClaimSummaryDirty(),
  loading: makeSelectProgressBarStatus(),
  stepWhatHappenedData: makeSelectStepWhatHappened(),
  stepCancellationAdditionalData: makeSelectStepCancellationAdditional(),
  stepPersonalItemsData: makeSelectStepPersonalItems(),
  stepOverseasMedicalData: makeSelectStepOverseasMedical(),
  stepRentalVehicleData: makeSelectStepRentalVehicle(),
  stepOtherData: makeSelectStepOther(),
  stepSupportingDocumentsData: makeSelectStepSupportingDocuments(),
  stepTripCancellationAndTripInterruptionData: makeSelectStepTripCancellationAndTripInterruption(),
  stepBaggageAndPersonalItemsData: makeSelectStepBaggageAndPersonalItems(),
  stepMedicalExpensesData: makeSelectStepMedicalExpenses(),
  stepPaymentDetailsData: makeSelectStepPaymentDetails(),
  stepBankDetailsData: makeSelectStepBankDetails(),
  stepMedicalExpenseData: makeSelectMedicalExpenseStep(),
  stepTripCancellationData: makeSelectTripCancellationStep(),
  stepTripInterruptionData: makeSelectTripInterruptionStep(),
  stepTripDelayData: makeSelectTripDelayStep(),
  stepPetMedicalData: makeSelectPetMedicalStep(),
  stepPropertyDamageData: makeSelectPropertyDamageStep(),
  stepCollisionDamageData: makeSelectCollisionDamageStep(),
  stepMissedConnectionData: makeSelectMissedConnectionStep(),
  stepSportsEquipmentData: makeSelectSportsEquipmentStep(),
  stepPersonalItemData: makeSelectPersonalItemStep(),
  stepMedicalExpenseCFData: makeSelectMedicalExpenseCFStep(),
  stepMedicalExpenseZurichData: makeSelectMedicalExpenseZurichStep(),
  stepItineraryChangeData: makeSelectItineraryChange(),
  stepBaggagePersonalEffectsData: makeSelectBaggagePersonalEffects(),
  stepMedicalHardshipData: makeSelectMedicalHardshipStep(),
  stepJobLossHardshipData: makeSelectJobLossHardshipStep(),
  stepDisasterAssistanceHardshipData: makeSelectDisasterAssistanceHardshipStep(),
  stepTravelDelayData: makeSelectStepTravelDelay(),
  stepMedicalAndEvacuationSCTIData: makeSelectMedicalAndEvacuationSCTIStep(),
  stepChangesToYourJourneySCTIData: makeSelectChangesToYourJourneySCTIStep(),
  stepBaggageAndPersonalItemsSCTIData: makeSelectBaggageAndPersonalItemsSCTIStep(),
  stepCashBankCardsTravelDocumentsAndPassportsData: makeSelectCashBankCardsTravelDocumentsAndPassportsStep(),
  stepPersonalAccidentSCTIData: makeSelectPersonalAccidentSCTIStep(),
  stepRentalVehicleExcessSCTIData: makeSelectRentalVehicleExcessSCTIStep(),
  stepChangesToYourJourneyData: makeSelectChangesToYourJourneyStep(),
  stepBaggageAndPersonalItemsStarrData: makeSelectStepBaggageAndPersonalItemsStarr(),
  stepOtherStarrData: makeSelectStepOtherStarr(),
  stepCollisionDamageStarrData: makeSelectCollisionDamageStarrStep(),
  stepMedicalExpensesTugoData: makeSelectMedicalExpensesTugoStep(),
  stepOtherTugoData: makeSelectStepOtherTugo(),
  stepBaseVictorsMedicalTugoData: makeSelectBaseVictorsMedicalTugo(),
  currentStep: makeSelectCurrentStep(),
  userInfo: makeSelectLoginInfo(),
  claimTypes: makeSelectAppClaimType(),
  jsonVersion: makeSelectJSONVersion(),
  isClaimGroupAllowed: makeSelectIsClaimGroupAllowed(),
  isClaimForFieldEnabled: makeSelectIsClaimForFieldEnabled(),
  showZurichUHC: makeSelectShowZurichUHC(),
  claimantId: makeSelectClaimantId(),
  insuredsList: makeSelectInsuredsList(),
  claimTypesInit: makeSelectInitClaimTypeList(),
  exchangeRates: makeSelectExchangeRates(),
  exchangeRatesLoading: makeSelectExchangeRatesLoading(),
  policyTypeCode: makeSelectPolicyTypeCode(),
  hasFormChanges: makeSelectFormHasChanges(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) =>
    dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onResetGivePermissionState: () => dispatch(resetFormGivePermissionState()),
  setFormHasChanges: (hasFormChanges) => dispatch(setFormHasChanges(hasFormChanges)),
  onFetchExchangeRates: (expenses) => dispatch(fetchExchangeRates(expenses)),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps
);
const withReducer = injectReducer({ key: 'stepClaimSummary', reducer });

export default compose(
  withReducer,
  withConnect,
  injectIntl
)(StepClaimSummary);



// WEBPACK FOOTER //
// ./app/containers/FastCover/StepClaimSummary/index.js